<template>
  <div class="pe-biz-sticky-tabs">
    <div ref="$tabsHeader" class="pe-biz-sticky-tabs-header">
      <div ref="$tabsHeaderContainer" class="pe-biz-sticky-tabs-header-container">
        <div
          v-for="(item, index) in getTabNames()"
          :key="index"
          :ref="(el) => (tabRefs[index] = el as HTMLElement)"
          :class="{ active: activeTabIndex === index, tab: true }"
          @click="scrollIntoView(index)"
        >
          {{ item }}
        </div>
      </div>
      <slot name="header"></slot>
    </div>
    <div ref="$tabsContent" class="pe-biz-sticky-tabs-content">
      <slot></slot>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  computed,
  ref,
  useSlots,
  onUpdated,
  onMounted,
  onUnmounted,
  unref,
  isRef,
  withDefaults,
  defineExpose,
  Fragment,
  watch,
  nextTick,
} from 'vue';
import StickyTab from './StickyTab.vue';
import type { Ref } from 'vue';

const activeTabIndex = ref(0);
const activeTabIndexClick = ref(-1);
const $tabsContent = ref<HTMLElement>();
const $tabsHeader = ref<HTMLElement>();
const $tabsHeaderContainer = ref<HTMLElement>();
const tabHeaderOffsets = ref<number[]>([]);
const tabRefs = ref<HTMLElement[]>([]);

const slots = useSlots();
function getTabNames() {
  const tabs: string[] = [];
  slots.default?.().forEach((node) => {
    if (node && node.type === StickyTab) tabs.push(node.props?.title);
    else if (node.type === Fragment) {
      Array.prototype.forEach.call(node.children, (item) => {
        if (item && item.type === StickyTab) tabs.push(item.props?.title);
      });
    }
  });

  return tabs;
}

type Props = {
  scrollThreshold?: number;
  root?: HTMLElement;
};
const props = withDefaults(defineProps<Props>(), {
  scrollThreshold: 20,
});
const emit = defineEmits<{
  (e: 'tabClick', index: number): void;
}>();

function updateTabPositions() {
  const offsetTop = $tabsHeader.value?.offsetHeight || 0;
  tabHeaderOffsets.value = Array.prototype.map.call(
    $tabsContent.value?.querySelectorAll('.pe-biz-sticky-tab'),
    (tab) => {
      return tab.offsetTop - offsetTop;
    }
  ) as number[];
}

onUpdated(() => {
  updateTabPositions();
});

const scrollListener = {
  handleEvent: () => {
    const target = rootToElement(props.root);
    for (let i = tabHeaderOffsets.value.length; i--; ) {
      if (
        ((target || document.scrollingElement)?.scrollTop || 0) + props.scrollThreshold >=
        tabHeaderOffsets.value[i]
      ) {
        activeTabIndex.value = i;
        nextTick(() => scrollToTab(i));
        break;
      }
    }
    if (activeTabIndexClick.value !== -1) {
      activeTabIndex.value = activeTabIndexClick.value;
      activeTabIndexClick.value = -1;
    }
  },
  options: {
    passive: true,
    capture: false,
  },
};

watch(
  () => props.root,
  (root) => {
    if (root) {
      const style = getComputedStyle(root);
      // need to set relative to get the calculation of children's offsetTop work
      if (style.position === 'static') {
        style.position = 'relative';
      }
    }
  },
  {
    immediate: true,
  }
);

onMounted(() => {
  const target = rootToElement(props.root);
  (target || window).addEventListener('scroll', scrollListener, scrollListener.options);
  updateTabPositions();
});

onUnmounted(() => {
  const target = rootToElement(props.root);
  (target || window).removeEventListener('scroll', scrollListener, scrollListener.options);
});

// 滚动到指定tab
function scrollToTab(index: number) {
  const tabElement = tabRefs.value[index];
  if (!tabElement || !$tabsHeaderContainer.value) return;

  // 计算需要滚动的距离
  const container = $tabsHeaderContainer.value;
  const containerRect = container.getBoundingClientRect();
  const tabRect = tabElement.getBoundingClientRect();

  const scrollLeft = tabRect.left - containerRect.left + container.scrollLeft;

  // 平滑滚动到指定位置
  container.scrollTo({
    left: scrollLeft,
    behavior: 'smooth',
  });
}

// 点击tab时的处理
function scrollIntoView(index: number) {
  activeTabIndex.value = index;
  activeTabIndexClick.value = index;
  updateTabPositions();
  const target = rootToElement(props.root);
  if (target) {
    target.scrollTop = tabHeaderOffsets.value[index];
  } else {
    window.scrollTo(0, tabHeaderOffsets.value[index]);
  }
  // 让当前选中的 tab 滚动到容器的最左侧
  const currentTab = tabRefs.value[index];
  if (currentTab) {
    currentTab.scrollIntoView({
      behavior: 'smooth', // 平滑滚动
      inline: 'start', // 对齐容器左侧
      block: 'nearest', // 避免垂直方向滚动
    });
  }
  emit('tabClick', index); // emit tab click event
}

function rootToElement(refOrElement?: Element | Ref<Element>) {
  if (!refOrElement) return refOrElement;
  if (refOrElement && isRef(refOrElement)) {
    return unref<Element>(refOrElement);
  }
  return refOrElement;
}
</script>
<style lang="scss">
.pe-biz-sticky-tabs {
  --text-color-primary: #050b27;
  --text-color-inactive: #999dae;

  color: var(--text-color-primary);
  .pe-biz-sticky-tabs-header {
    position: sticky;
    background-color: #fff;
    top: var(--sticky-top, 0);

    .pe-biz-sticky-tabs-header-container {
      display: flex;
      font-size: 14px;
      font-weight: 500;
      color: var(--text-color-inactive);
      align-items: center;
      height: 44px;
      line-height: 44px;
      overflow-x: auto;
    }

    .tab {
      margin-right: 30px;
      cursor: pointer;
      word-wrap: break-word;
      white-space: nowrap;
    }

    .active {
      font-size: 18px;
      color: var(--text-color-primary);
    }
  }
}
</style>